import { useState, useEffect, useCallback } from "react";
import {
  _get_support_officer_progress_dashboard,
  _get_support_officer_sales_dashboard,
  _get_support_officer_daily_breakdown,
  _get_support_dashboard_home,
  _get_new_support_orders,
} from "../../../common/axios_services";

const useSupportOfficerData = () => {
  const [progressData, setProgressData] = useState({});
  const [salesData, setSalesData] = useState({});
  const [dashboardStats, setDashboardStats] = useState({});
  const [orders, setOrders] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 10, total: 0 });
  const [dailyBreakdown, setDailyBreakdown] = useState({
    days: [],
    week: "",
    total: 0,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [dateRange, setDateRange] = useState({
    dateFrom: null,
    dateTo: null,
  });

  const getDefaultDates = useCallback(() => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, "0");
    const day = String(today.getDate()).padStart(2, "0");
    const dateStr = `${year}-${month}-${day}`;
    return { dateFrom: dateStr, dateTo: dateStr };
  }, []);

  const fetchProgressDashboard = useCallback(
    async (dateFrom, dateTo, supportManagerId, supportOfficerId) => {
      try {
        setLoading(true);
        setError(null);
        const res = await _get_support_officer_progress_dashboard(
          dateFrom,
          dateTo,
          supportManagerId,
          supportOfficerId,
        );
        setProgressData(res?.data?.data || {});
        return { success: true, data: res?.data?.data };
      } catch (err) {
        const errorMessage = err.message || "Failed to load progress dashboard";
        setError(errorMessage);
        return { success: false, error: errorMessage };
      } finally {
        setLoading(false);
      }
    }, []);

  const fetchSalesDashboard = useCallback(
    async (dateFrom, dateTo, supportManagerId, supportOfficerId) => {
      try {
        setLoading(true);
        setError(null);
        const res = await _get_support_officer_sales_dashboard(
          dateFrom,
          dateTo,
          supportManagerId,
          supportOfficerId,
        );
        setSalesData(res?.data?.data || {});
        return { success: true, data: res?.data?.data };
      } catch (err) {
        const errorMessage = err.message || "Failed to load sales dashboard";
        setError(errorMessage);
        return { success: false, error: errorMessage };
      } finally {
        setLoading(false);
      }
    }, []);

  const fetchDailyBreakdown = useCallback(async (dateFrom, dateTo) => {
    try {
      setLoading(true);
      setError(null);
      const res = await _get_support_officer_daily_breakdown(dateFrom, dateTo);
      setDailyBreakdown(res?.data?.data || { days: [], week: "", total: 0 });
      return { success: true, data: res?.data?.data };
    } catch (err) {
      const errorMessage = err.message || "Failed to load daily breakdown";
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchOrders = useCallback(
    async (tab, page, limit, supportManagerId, supportOfficerId) => {
      try {
        setLoading(true);
        setError(null);
        const { dateFrom } = getDefaultDates();
        const res = await _get_new_support_orders(
          tab,
          page,
          limit,
          supportManagerId,
          supportOfficerId,
          dateFrom
        );
        setOrders(res?.data?.data?.orders || []);
        setPagination(
          res?.data?.data?.pagination || { page: 1, limit: 10, total: 0 },
        );
        return { success: true, data: res?.data?.data };
      } catch (err) {
        const errorMessage = err.message || "Failed to load orders";
        setError(errorMessage);
        return { success: false, error: errorMessage };
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const fetchAllData = useCallback(async (dateFrom, dateTo) => {
    try {
      setLoading(true);
      setError(null);
      const [progressRes, salesRes, breakdownRes, dashboardRes] =
        await Promise.all([
          _get_support_officer_progress_dashboard(dateFrom, dateTo),
          _get_support_officer_sales_dashboard(dateFrom, dateTo),
          _get_support_officer_daily_breakdown(dateFrom, dateTo),
          _get_support_dashboard_home(),
        ]);
      setProgressData(progressRes?.data?.data || {});
      setSalesData(salesRes?.data?.data || {});
      setDailyBreakdown(
        breakdownRes?.data?.data || { days: [], week: "", total: 0 },
      );
      setDashboardStats(dashboardRes?.data?.data || {});
      setDateRange({ dateFrom, dateTo });
      return { success: true };
    } catch (err) {
      const errorMessage = err.message || "Failed to load dashboard data";
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const defaultDates = getDefaultDates();
    fetchAllData(defaultDates.dateFrom, defaultDates.dateTo);
  }, [getDefaultDates, fetchAllData]);

  return {
    progressData,
    salesData,
    dashboardStats,
    dailyBreakdown,
    loading,
    error,
    dateRange,
    fetchAllData,
    fetchProgressDashboard,
    fetchSalesDashboard,
    fetchDailyBreakdown,
    fetchOrders,
    getDefaultDates,
    orders,
    pagination,
  };
};

export default useSupportOfficerData;
